// import components
import { Chip, CircularProgress, Tooltip } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledBoxFlexCenterSmallGap,
  StyledBoxFlexColumnCenterSmallGap,
  StyledBoxFlexColumnNoGap,
  StyledBoxFlexSmallGap
} from 'components/shared/Box/Box.styles';
import {
  StyledFormattedDateBox,
  StyledNoteAvatar,
  StyledSingleNoteContainerBox
} from './NoteList.styles';

// import types
import { NoteCardProps } from './NoteCard.types';

// import utils
import dayjs from 'dayjs';
import { useSelector } from 'hooks';
import { t } from 'i18next';
import { isExternalNote, isPrivateNote } from './NoteList.utils';
import {
  StyledCapitalizeTypography,
  StyledCustomCapTypography,
  StyledMediumIconButton,
  StyledNoteNameAndDateBox,
  StyledTooltipContainer
} from './NoteCard.styles';
import { StyledTypographyLineClamped } from 'components/shared/Typography/Typography.styles';
import { StyledCustomMatchDrawerTooltip } from 'components/flex-portal/FlexMatch/components/MatchDrawerInfoContent.styles';

export default function NoteCard({
  note,
  onDelete,
  deleting,
  isEmployeePortal,
  noteVariant,
  isCompact = false,
  showTooltip = false
}: NoteCardProps) {
  const { hasDeleteNotes } = useSelector(({ grants }) => grants);
  const formattedDate = dayjs(note.creationDate).format('DD MMM. YYYY · HH:mm');
  const firstLetter = note.creationUser?.trim().charAt(0).toUpperCase() || '?';

  return (
    <StyledCustomMatchDrawerTooltip
      title={
        <StyledTooltipContainer>
          <StyledCustomCapTypography variant="body2">
            {note.creationUser}
          </StyledCustomCapTypography>
          <StyledFormattedDateBox isCompact={isCompact}>
            {formattedDate}
          </StyledFormattedDateBox>
          {note.content}
        </StyledTooltipContainer>
      }
      arrow
      disableHoverListener={!showTooltip}
    >
      <StyledSingleNoteContainerBox
        noteVariant={noteVariant}
        isCompact={isCompact}
      >
        <StyledBoxFlexSmallGap>
          <StyledNoteAvatar isCompact={isCompact}>
            {firstLetter}
          </StyledNoteAvatar>

          <StyledBoxFlexColumnNoGap>
            <StyledNoteNameAndDateBox isCompact={isCompact}>
              <StyledCapitalizeTypography variant={isCompact ? 'body1' : 'h6'}>
                {note.creationUser}
              </StyledCapitalizeTypography>

              <StyledFormattedDateBox isCompact={isCompact}>
                {formattedDate}
              </StyledFormattedDateBox>
            </StyledNoteNameAndDateBox>
            {showTooltip ? (
              <StyledTypographyLineClamped lineClamp={1}>
                {note.content}
              </StyledTypographyLineClamped>
            ) : (
              note.content
            )}
          </StyledBoxFlexColumnNoGap>
        </StyledBoxFlexSmallGap>

        <StyledBoxFlexColumnCenterSmallGap>
          <StyledBoxFlexCenterSmallGap>
            {(isPrivateNote(note) || isExternalNote(note)) && (
              <Tooltip
                title={
                  isPrivateNote(note)
                    ? t('noteIsPrivate')
                    : t('noteIsVisibleForExternal')
                }
                arrow
              >
                <Chip
                  color={isPrivateNote(note) ? 'warning' : 'info'}
                  size="small"
                  label={isPrivateNote(note) ? t('private') : t('external')}
                />
              </Tooltip>
            )}

            {!isEmployeePortal && hasDeleteNotes && (
              <Tooltip title={t('deleteNote')} arrow>
                <StyledMediumIconButton
                  size="small"
                  aria-label="delete-note-button"
                  onClick={onDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Icon type="trash-can" fontSize="small" />
                  )}
                </StyledMediumIconButton>
              </Tooltip>
            )}
          </StyledBoxFlexCenterSmallGap>
        </StyledBoxFlexColumnCenterSmallGap>
      </StyledSingleNoteContainerBox>
    </StyledCustomMatchDrawerTooltip>
  );
}
